import * as React from 'react';
import {
    getSessionQr,
    isLoggedIn,
    loginQr,
    logout,
    setLoginCookies,
    testLogin,
    testLoginDelay
} from 'services/authentication/authentication-service';
import {RouteComponentProps, withRouter} from 'react-router';
import {
    AuthenticationState,
    BankIdSession,
    BankIdStart,
    BankIdStatus,
    User
} from 'services/authentication/types';
import {IsMobile, sleep} from 'services/utils/helpers';
import {ENV, getCurrentEnv} from 'services/constants';


export interface AuthenticationQrContextType {
    user: User,
    loggedIn: boolean;
    loginStarted: boolean;
    autoStartBankId: boolean;
    authenticationState: AuthenticationState;
    qrCode: string;
    isMobile: boolean;
    bankIdOnFile: boolean;
    bankIdOnFileAvailable: boolean;
    setLoggedIn: (source: string, autoStartBankId: boolean, bankIdOnFile?: boolean) => any;
    setLogout: () => any;
    cancelLogin: () => any;
    errorMessage: string;
}

interface Props extends RouteComponentProps {
    children: any;
}

/**
 * BankID på fil (BankID-säkerhetsprogrammet på datorn) för att underlätta test. Går mot Scrive testbed
 * med test-BankID och finns bara på desktop i local, dev och test. IdentityService avvisar flaggan i prod.
 * Inte på de gamla CDN-adresserna (*.azureedge.net), bara på dev-brfweb.jm.se och test-brfweb.jm.se.
 */
export const isBankIdOnFileAvailable = (isMobile: boolean) => {
    const env = getCurrentEnv();
    return !isMobile
        && !window.location.hostname.endsWith('.azureedge.net')
        && (env === ENV.LOCAL || env === ENV.DEV || env === ENV.TEST);
};

// @ts-ignore
export const AuthenticationQrContext = React.createContext<AuthenticationQrContextType>();
export const AuthenticationQrConsumer = AuthenticationQrContext.Consumer;

export class AuthenticationQrProviderComp extends React.PureComponent<Props, AuthenticationQrContextType> {

    // Ökas vid varje ny inloggning och vid avbryt, så att en tidigare pollning slutar.
    loginAttempt = 0;

    async componentDidMount() {

        if(window.location.href.indexOf('poolingId') > -1) {
            try {
                let poolingId = window.location.search.replace('?poolingId=', '');
    
                let i = 0;
    
                while (i < 30) {
                    i++;
                    await sleep(1000);
                    let poolingResponse = await getSessionQr(poolingId);
    
                    if(poolingResponse.authorized) {
                        this.handleBankIdSession(poolingResponse);
                        break;
                    }
                }
            } catch (e) {

            }
        }
    }

    cancelLogin = async () => {
        this.loginAttempt++;

        this.setState({
            user: null,
            authenticationState: AuthenticationState.IDLE,
            loggedIn: false,
            loginStarted: false,
            bankIdOnFile: false,
        })
    };

    setLoggedIn = async (source: string, autoStartBankId: boolean, bankIdOnFile: boolean = false) => {

        const attempt = ++this.loginAttempt;

        let env = getCurrentEnv();

        let useTestLogin = false;
        let isTestingEnvironment = env === ENV.LOCAL || env === ENV.DEV || env === ENV.TEST;

        this.setState({
            loginStarted: true,
            autoStartBankId: autoStartBankId,
            bankIdOnFile: bankIdOnFile,
        });

        let loginResult : BankIdStart = (env === ENV.PROD || !useTestLogin) ? await loginQr(bankIdOnFile) : await testLoginDelay();

        if (attempt !== this.loginAttempt) return;

        if(isTestingEnvironment || !useTestLogin || (loginResult !== null && loginResult.sessionId)) {

            if(this.state.isMobile && autoStartBankId) {
                //window.history.replaceState({}, document.title, window.location.origin + window.location.pathname);

                let poolingUrl = window.location.origin + window.location.pathname + `?poolingId=${loginResult.sessionId}`

                window.history.replaceState({}, document.title, poolingUrl);

                window.location.href = `https://app.bankid.com/?autostarttoken=${loginResult.autoStartToken}&redirect=${encodeURIComponent(poolingUrl)}`; //&redirect=${redirectUrl}
                
                return;
            }

            let statusResponse: BankIdSession;

            this.setState({
                authenticationState: AuthenticationState.POOLING,
                qrCode: bankIdOnFile ? null : loginResult.qrCode
            }, async () => {

                // BankID på fil: starta BankID-programmet på datorn och polla som vanligt.
                if (bankIdOnFile) {
                    window.location.href = `bankid:///?autostarttoken=${loginResult.autoStartToken}&redirect=null`;
                }

                let i = 0;

                while(i < 300) {
                    i++;

                    try{
                        await sleep(2000);

                        if (attempt !== this.loginAttempt) return;

                        statusResponse = isTestingEnvironment && useTestLogin ? await testLogin("") : await getSessionQr(loginResult.sessionId);

                        if (attempt !== this.loginAttempt) return;

                        if (!bankIdOnFile) {
                            this.setState({
                                qrCode: statusResponse.qrCode
                            });
                        }
    
                        if(statusResponse.authorized) {
                            break;
                        }
                    }
                    catch{
                        console.log("retry");
                    }

                }

                this.handleBankIdSession(statusResponse);
            });
        }
    };

    handleBankIdSession = (bankIdSession:BankIdSession) => {
        if(bankIdSession.status === BankIdStatus.Ok && bankIdSession.token) {

            setLoginCookies(bankIdSession.token);

            this.setState({
                user: null,
                loggedIn: true,
                authenticationState: AuthenticationState.IDLE,
                loginStarted: false,
            }, () => {
                this.props.history.push('/');
            });
        }

        if(bankIdSession.status === BankIdStatus.Error || !bankIdSession.token) {
            this.setState({
                authenticationState: AuthenticationState.IDLE,
                loggedIn: false,
                loginStarted: false,
                errorMessage: 'Ingen användare'
            })
        }
    }

    setLogout = () => {
        this.setState({
            user: null,
            loggedIn: false,
        }, () => {
            logout();
        });
    };

    state = {
        user: null,
        loginStarted: false,
        autoStartBankId: false,
        loggedIn: isLoggedIn(),
        setLoggedIn:  this.setLoggedIn,
        setLogout: this.setLogout,
        cancelLogin: this.cancelLogin,
        errorMessage: '',
        authenticationState: AuthenticationState.IDLE,
        qrCode: null,
        isMobile: IsMobile(),
        bankIdOnFile: false,
        bankIdOnFileAvailable: isBankIdOnFileAvailable(IsMobile()),
    };

    render() {

        return (
            <AuthenticationQrContext.Provider value={this.state}>
                {this.props.children}
            </AuthenticationQrContext.Provider>
        );
    }

}

export const AuthenticationQrProvider = withRouter(AuthenticationQrProviderComp);
